import { useMemo, useState } from "react";
import { StyleProp, View, ViewStyle } from "react-native";
import SeededRandomGenerator from "src/helpers/SeededRandomGenerator";
import AnimatedScatteredSticker from "./components/AnimatedScatteredSticker";
import ScatteredSticker from "./components/ScatteredSticker";
import { ParsedScatteredSticker } from "./types";

// Ensure the stickers are decently far apart + account for rotation
const SizeVariance = 0.6;
const DegreeRange = 20;
const StickersScattered = ({
	stickers,
	style,
	sizeFactor = 0.3,
	animated: animatedProp,
	collisionMargin = 0.25,
}: {
	stickers: { url: string | null | undefined }[];
	style?: StyleProp<ViewStyle>;
	/**
	 * @default 0.3
	 */
	sizeFactor?: number;
	animated?: boolean;
	/**
	 * @default 0.25
	 */
	collisionMargin?: number;
}) => {
	const [containerDim, setContainerDim] = useState<{
		width: number;
		height: number;
	} | null>(null);
	const random = useMemo(() => {
		const generator = new SeededRandomGenerator(0);
		return () => generator.random();
	}, []);
	const { parsedStickers } = useMemo<{
		parsedStickers: ParsedScatteredSticker[];
	}>(() => {
		const parsedStickers: ParsedScatteredSticker[] = [];
		const validStickers = stickers.filter((sticker): sticker is { url: string } =>
			Boolean(sticker.url),
		);
		if (validStickers.length === 0 || containerDim == null) return { parsedStickers };

		const { width, height } = containerDim;
		const baseSize = sizeFactor * Math.min(width, height);
		const isColliding = (sticker: ParsedScatteredSticker) => {
			for (const otherSticker of parsedStickers) {
				if (
					sticker.x + sticker.size * (1 + collisionMargin) > otherSticker.x &&
					otherSticker.x + otherSticker.size * (1 + collisionMargin) > sticker.x &&
					sticker.y + sticker.size * (1 + collisionMargin) > otherSticker.y &&
					otherSticker.y + otherSticker.size * (1 + collisionMargin) > sticker.y
				) {
					return true;
				}
			}
			return false;
		};
		for (let i = 0; i < Math.min(100, Math.floor(0.0005 * width * height)); i++) {
			const sticker = validStickers[parsedStickers.length % validStickers.length];
			if (!sticker) continue;
			const animated = animatedProp ? random() < 0.4 : false;
			const stickerCandidate: ParsedScatteredSticker = {
				url: sticker.url,
				x: (random() - 0.5) * width,
				y: (random() - 0.5) * height,
				size: (SizeVariance * random() + (1 - SizeVariance)) * baseSize,
				rotation: (random() - 0.5) * DegreeRange,
				animated,
				animationDelay: animated ? Math.floor(random() * 2000) : 0,
				animationReverse: animated ? random() < 0.5 : false,
			};
			if (isColliding(stickerCandidate)) continue;
			parsedStickers.push(stickerCandidate);
		}

		return { parsedStickers };
	}, [stickers, containerDim, sizeFactor, random, animatedProp, collisionMargin]);

	return (
		<View
			style={[
				{
					overflow: "hidden",
				},
				style,
				{
					alignItems: "center",
					justifyContent: "center",
				},
			]}
			onLayout={(event) => {
				if (
					containerDim &&
					Math.abs(containerDim.width - event.nativeEvent.layout.width) < 100
				) {
					return;
				}
				setContainerDim({
					width: event.nativeEvent.layout.width,
					height: event.nativeEvent.layout.height,
				});
			}}
		>
			{parsedStickers.map((sticker, index) =>
				sticker.animated ? (
					<AnimatedScatteredSticker key={index} sticker={sticker} />
				) : (
					<ScatteredSticker key={index} sticker={sticker} />
				),
			)}
		</View>
	);
};
export default StickersScattered;
